import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import Badge from "@/components/base/Badge";
import Select from "@/components/base/Select";
import ExecutionFlow from "@/pages/provider/orders/components/ExecutionFlow";
import { teamMembers, drivers } from "@/mocks/team";
import type { Order, OrderTab, OrderTimeline } from "@/mocks/orders";

interface OrderDetailProps {
  order: Order;
}

const tabBadge: Record<
  OrderTab,
  { tone: "amber" | "teal" | "green" | "red"; label: string; pulse?: boolean }
> = {
  new: { tone: "amber", label: "جديد" },
  scheduled: { tone: "teal", label: "مقبول" },
  current: { tone: "teal", label: "قيد التنفيذ", pulse: true },
  completed: { tone: "green", label: "مكتمل" },
  cancelled: { tone: "red", label: "ملغي" },
};

const serviceIcon: Record<Order["serviceType"], string> = {
  "غسيل سيارات": "ri-car-line",
  "تنظيف منازل": "ri-home-4-line",
  "مغاسل ملابس": "ri-t-shirt-line",
};

const photoConfig: Record<
  Order["serviceType"],
  { title: string; before: string; after: string }
> = {
  "غسيل سيارات": { title: "صور قبل وبعد", before: "قبل الغسيل", after: "بعد الغسيل" },
  "تنظيف منازل": { title: "صور قبل وبعد", before: "قبل التنظيف", after: "بعد التنظيف" },
  "مغاسل ملابس": { title: "صور القطع", before: "عند الاستلام من العميل", after: "عند التسليم للعميل" },
};

const techStatus: Record<string, { label: string; className: string }> = {
  available: { label: "متاح", className: "text-[#27AE60]" },
  busy: { label: "مشغول", className: "text-[#B45309]" },
  vacation: { label: "إجازة", className: "text-ghasla-text-secondary" },
};

const courierTone: Record<"teal" | "gray" | "blue", "teal" | "gray" | "blue"> = {
  teal: "teal",
  gray: "gray",
  blue: "blue",
};

function Section({
  icon,
  title,
  extra,
  children,
  className = "",
}: {
  icon: string;
  title: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`flex h-full flex-col rounded-2xl border border-[#F0F4F4] bg-white p-4 md:p-5 ${className}`}>
      <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
          <i className={`${icon} text-sm`} />
        </span>
        {title}
        {extra}
      </h3>
      <div className="mt-4 flex-1">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="shrink-0 text-[13px] text-ghasla-text-secondary">{label}</span>
      <div className="min-w-0 text-[15px] text-ghasla-text-primary">{children}</div>
    </div>
  );
}

function Timeline({ items }: { items: OrderTimeline[] }) {
  return (
    <div className="space-y-0">
      {items.map((t, i) => {
        const status = t.status ?? "done";
        const isCurrent = status === "current";
        const isPending = status === "pending";
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              {isCurrent ? (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00AAB7] text-white animate-pulse">
                  <i className="ri-flashlight-line text-[11px]" />
                </span>
              ) : isPending ? (
                <span className="h-5 w-5 rounded-full border-2 border-[#E4F5F5] bg-white" />
              ) : (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E8F6F6] text-ghasla-text-link">
                  <i className="ri-check-line text-xs" />
                </span>
              )}
              {i < items.length - 1 && (
                <span className={`w-px flex-1 ${isCurrent ? "bg-[#00AAB7]" : "bg-[#E4F5F5]"}`} />
              )}
            </div>
            <div className="pb-4">
              <p
                className={`text-sm ${
                  isCurrent
                    ? "font-semibold text-ghasla-text-link"
                    : isPending
                    ? "font-normal text-ghasla-text-secondary"
                    : "font-medium text-ghasla-text-heading"
                }`}
              >
                {t.label}
              </p>
              {t.time && <p className="text-xs text-ghasla-text-secondary">{t.time}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OrderDetail({ order }: OrderDetailProps) {
  const navigate = useNavigate();
  const amountNum = Number(order.amount) || 0;
  const commission = order.commission ?? (Math.round(amountNum * 0.15 * 100) / 100).toFixed(2);
  const net = order.net ?? (Math.round(amountNum * 0.85 * 100) / 100).toFixed(2);
  const isLaundry = order.serviceType === "مغاسل ملابس";
  const totalPieces = (order.laundryItems ?? []).reduce((s, i) => s + i.count, 0);

  const photoCfg = photoConfig[order.serviceType];
  const beforePhotos = isLaundry
    ? order.pickupPhotos ?? []
    : order.proofBefore
    ? [order.proofBefore]
    : [];
  const afterPhotos = isLaundry
    ? order.deliveryPhotos ?? []
    : order.proofAfter
    ? [order.proofAfter]
    : [];
  const showPhotos = order.tab === "current" || order.tab === "completed";
  const showAfter = order.tab === "completed";

  const [assignedTech, setAssignedTech] = useState(order.assignedTech ?? order.assignee ?? "");
  const [assignedCourier, setAssignedCourier] = useState(order.pickupCourier?.name ?? "");
  const [selectedTech, setSelectedTech] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [decision, setDecision] = useState<"accept" | "reject" | null>(null);

  const assignedName = isLaundry ? assignedCourier : assignedTech;

  const assignOptions = isLaundry
    ? drivers.map((d) => ({ value: d.name, label: `${d.name} — ${d.status} ★ ${d.rating}` }))
    : teamMembers.map((m) => ({ value: m.name, label: `${m.name} — ${techStatus[m.status].label}` }));

  const badge =
    order.tab === "new" && decision === "accept"
      ? { tone: "teal" as const, label: "مقبول" }
      : tabBadge[order.tab];

  const displayTimeline: OrderTimeline[] = [
    ...order.timeline,
    ...(order.tab === "new" && decision === "accept"
      ? [{ time: "الآن", label: "تم قبول الطلب", status: "done" as const }]
      : []),
    ...(order.tab === "new" && decision === "accept" && assignedName
      ? [
          {
            time: "الآن",
            label: `تم تعيين ${isLaundry ? "مندوب الاستلام" : "المنفذ"} — ${assignedName}`,
            status: "current" as const,
          },
        ]
      : []),
  ];

  const handleAssign = () => {
    if (!selectedTech) return;
    setAssignedTech(selectedTech);
    setSelectedTech("");
  };

  const handleFlowAssign = () => {
    if (!selectedAssignee) return;
    if (isLaundry) setAssignedCourier(selectedAssignee);
    else setAssignedTech(selectedAssignee);
    setSelectedAssignee("");
  };

  const headerDate = order.date ?? order.schedule;

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/provider/orders")}
        className="mb-5 inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ghasla-text-secondary transition-colors hover:text-ghasla-text-link"
      >
        <i className="ri-arrow-right-line text-lg" />
        العودة للطلبات
      </button>

      {/* Header */}
      <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-4 shadow-card md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-bold text-ghasla-text-heading md:text-[24px]">الطلب #{order.id}</h1>
              <Badge tone={badge.tone} pulse={badge.pulse}>
                {badge.label}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-ghasla-text-secondary">{headerDate}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#E8F6F6] px-3 py-1 text-xs font-medium text-ghasla-text-link">
              <i className={`${serviceIcon[order.serviceType]} text-sm`} />
              {order.serviceType}
            </span>
          </div>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#007A85]">
            <img
              src="https://storage.helloreaddy.io/project_files/9818337a-0a98-464f-85b4-cd986046343e/9ac90c09-7871-46ab-ad1d-a8594706691c_compressed_ChatGPT-Image-2--2026-11_08_10--1.webp"
              alt="غسلة"
              className="h-6 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mt-4 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          {/* 1 — بيانات العميل */}
          <Section icon="ri-user-3-line" title="بيانات العميل">
            <Row label="الاسم">
              <span className="font-semibold text-ghasla-text-heading">{order.customer}</span>
            </Row>
            <Row label="الجوال">
              <span className="flex items-center gap-2">
                <span dir="ltr" className="font-medium">{order.phone}</span>
                <button
                  type="button"
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-[#E8F6F6] text-ghasla-text-link transition-opacity hover:opacity-80"
                  aria-label="اتصال"
                >
                  <i className="ri-phone-line text-sm" />
                </button>
                <button
                  type="button"
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-[#E8F9F0] text-[#27AE60] transition-opacity hover:opacity-80"
                  aria-label="واتساب"
                >
                  <i className="ri-whatsapp-line text-sm" />
                </button>
              </span>
            </Row>
            <Row label="الموقع">
              <span className="flex items-center gap-1.5">
                <span>{order.address}</span>
                <button
                  type="button"
                  className="cursor-pointer whitespace-nowrap text-xs font-medium text-ghasla-text-link hover:underline"
                >
                  خريطة
                </button>
              </span>
            </Row>
            <Row label="ملاحظات الموقع">
              {order.locationNote && order.locationNote !== "لا يوجد" ? (
                <span>{order.locationNote}</span>
              ) : (
                <span className="text-ghasla-text-secondary">لا يوجد</span>
              )}
            </Row>
          </Section>

          {/* 2 — تفاصيل التنظيف (تنظيف منازل) */}
          {order.serviceType === "تنظيف منازل" && (
            <>
              <Section icon="ri-home-4-line" title="تفاصيل التنظيف">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                    order.cleaningType === "شامل"
                      ? "bg-[#E8F6F6] text-ghasla-text-link"
                      : "bg-[#EFF6FF] text-[#1D4ED8]"
                  }`}
                >
                  <i className="ri-home-smile-line text-sm" />
                  {order.cleaningType === "شامل" ? "تنظيف شامل" : "عاملة بالساعة"}
                </span>

                {order.cleaningType === "شامل" && order.cleaningElements ? (
                  <div className="mt-4">
                    <div className="space-y-2.5">
                      {order.cleaningElements.map((el) => (
                        <div key={el.name} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                              <i className={`${el.icon} text-sm`} />
                            </span>
                            <span className="text-sm text-ghasla-text-primary">{el.name}</span>
                            <span className="text-xs text-ghasla-text-secondary">×{el.count}</span>
                          </div>
                          <span className="whitespace-nowrap text-sm font-medium text-ghasla-text-primary">
                            {el.price} ر.س
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-[#F0F4F4] pt-3">
                      <span className="text-sm font-medium text-ghasla-text-secondary">
                        الإجمالي ({order.cleaningElements.reduce((s, e) => s + e.count, 0)} عناصر)
                      </span>
                      <span className="text-[20px] font-bold text-ghasla-text-link">{order.amount} ر.س</span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ghasla-text-primary">عدد الساعات</span>
                      <span className="text-sm font-semibold text-ghasla-text-heading">{order.hours} ساعات</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ghasla-text-primary">سعر الساعة</span>
                      <span className="text-sm text-ghasla-text-primary">{order.hourlyRate} ر.س</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-[#F0F4F4] pt-3">
                      <span className="text-sm font-medium text-ghasla-text-secondary">الإجمالي</span>
                      <span className="text-[20px] font-bold text-ghasla-text-link">{order.amount} ر.س</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#FFF9EB] px-3 py-2 text-xs text-[#B45309]">
                      <i className="ri-alert-line" />
                      المواد والأدوات من العميل
                    </div>
                  </div>
                )}
              </Section>

              <Section icon="ri-edit-2-line" title="ملاحظات العميل">
                {order.customerNotes ? (
                  <div className="rounded-xl bg-[#F4FAFA] p-3.5 text-[15px] text-ghasla-text-primary">
                    {order.customerNotes}
                  </div>
                ) : (
                  <p className="text-sm italic text-ghasla-text-secondary">لا توجد ملاحظات</p>
                )}
              </Section>
            </>
          )}

          {/* 2 — بيانات السيارة + الخدمة (غسيل سيارات) */}
          {order.serviceType === "غسيل سيارات" && (
            <>
              <Section icon="ri-car-line" title="بيانات السيارة">
                <Row label="نوع السيارة">
                  <span className="font-medium">{order.carType ?? "غير محدد"}</span>
                </Row>
                <Row label="رقم اللوحة">
                  {order.carPlate ? (
                    <span
                      dir="ltr"
                      className="inline-block rounded-md border-2 border-[#E4F5F5] bg-[#F0FDFD] px-3 py-0.5 font-mono text-sm font-bold tracking-wider text-ghasla-text-heading"
                    >
                      {order.carPlate}
                    </span>
                  ) : (
                    <span className="text-ghasla-text-secondary">لا يوجد</span>
                  )}
                </Row>
                <Row label="صورة السيارة">
                  {order.carPhoto ? (
                    <button
                      type="button"
                      onClick={() => setLightbox(order.carPhoto ?? "")}
                      className="block h-20 w-20 cursor-pointer overflow-hidden rounded-lg"
                    >
                      <img
                        src={order.carPhoto}
                        alt={`${order.carType ?? "السيارة"} - غسلة`}
                        title={`${order.carType ?? "السيارة"} - غسلة`}
                        className="h-full w-full object-cover object-top"
                      />
                    </button>
                  ) : (
                    <span className="text-ghasla-text-secondary">لا يوجد</span>
                  )}
                </Row>
              </Section>

              <Section icon="ri-sparkling-line" title="الخدمة المطلوبة">
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-semibold text-ghasla-text-heading">{order.service}</span>
                  <span className="text-xs text-ghasla-text-secondary">{order.serviceDuration ?? "—"}</span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-ghasla-text-secondary">سعر الخدمة</span>
                  <span className="text-sm font-semibold text-ghasla-text-primary">{order.basePrice ?? order.amount} ر.س</span>
                </div>

                {order.options && order.options.length > 0 && (
                  <div className="mt-3 border-t border-[#F0F4F4] pt-3">
                    <p className="text-sm font-semibold text-ghasla-text-heading">الخيارات الإضافية</p>
                    <div className="mt-2 space-y-1.5">
                      {order.options.map((opt) => (
                        <div key={opt.label} className="flex items-center justify-between">
                          <span className="text-sm text-ghasla-text-primary">{opt.label}</span>
                          <span className="text-sm font-medium text-ghasla-text-link">+{opt.price} ر.س</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between border-t border-[#F0F4F4] pt-3">
                  <span className="text-sm font-medium text-ghasla-text-secondary">إجمالي الطلب</span>
                  <span className="text-[20px] font-bold text-ghasla-text-link">{order.amount} ر.س</span>
                </div>
              </Section>
            </>
          )}

          {/* 2/3/4 — القطع + التوصيل + المناديب (مغاسل ملابس) */}
          {isLaundry && (
            <>
              {/* القطع */}
              <Section
                icon="ri-t-shirt-line"
                title="تفاصيل القطع"
                extra={<Badge tone="gray">{totalPieces} قطع</Badge>}
              >
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="border-b border-[#F0F4F4] text-right text-xs font-medium text-ghasla-text-secondary">
                        <th className="py-2 pl-2">القطعة</th>
                        <th className="px-2 py-2">الخدمة</th>
                        <th className="px-2 py-2">الخيارات</th>
                        <th className="px-2 py-2">نوع الغسيل</th>
                        <th className="px-2 py-2">الكمية</th>
                        <th className="py-2 pr-2">السعر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.laundryItems?.map((item) => (
                        <tr
                          key={`${item.name}-${item.service}`}
                          className="border-b border-[#F4FAFA]"
                        >
                          <td className="py-2.5 pl-2">
                            <span className="flex items-center gap-2">
                              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#F0FDFD] text-ghasla-text-link">
                                <i className={`${item.icon} text-xs`} />
                              </span>
                              <span className="whitespace-nowrap font-medium text-ghasla-text-primary">{item.name}</span>
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-2 py-2.5 text-ghasla-text-primary">{item.service}</td>
                          <td className="px-2 py-2.5">
                            {item.options ? (
                              <span className="inline-flex items-center whitespace-nowrap rounded-full bg-[#FFF9EB] px-2 py-0.5 text-xs text-[#B45309]">
                                {item.options}
                              </span>
                            ) : (
                              <span className="text-ghasla-text-secondary">—</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2.5 text-ghasla-text-primary">{item.washType}</td>
                          <td className="whitespace-nowrap px-2 py-2.5 text-ghasla-text-primary">×{item.count}</td>
                          <td className="whitespace-nowrap py-2.5 pr-2 font-semibold text-ghasla-text-heading">{item.price} ر.س</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-[#F0F4F4] pt-3">
                  <span className="text-sm font-medium text-ghasla-text-secondary">المجموع الفرعي ({totalPieces} قطع)</span>
                  <span className="text-[15px] font-semibold text-ghasla-text-heading">{order.laundrySubtotal} ر.س</span>
                </div>
              </Section>

              {/* التوصيل */}
              <Section icon="ri-truck-line" title="تفاصيل التوصيل">
                <Row label="نوع التوصيل">
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{order.delivery?.typeLabel}</span>
                    {order.delivery?.type === "مستعجل" && <Badge tone="amber">مستعجل</Badge>}
                  </span>
                </Row>
                <Row label="رسوم التوصيل">
                  <span className="font-medium">{order.delivery?.fee} ر.س</span>
                </Row>
                <Row label="عنوان الاستلام">
                  <span>{order.delivery?.pickupAddress}</span>
                </Row>
                <Row label="عنوان التسليم">
                  <span>{order.delivery?.deliveryAddress}</span>
                </Row>
                <Row label="التسليم المتوقع">
                  <span className="font-semibold text-ghasla-text-heading">{order.delivery?.expectedDelivery}</span>
                </Row>
              </Section>

              {/* المناديب */}
              <Section icon="ri-information-line" title="مناديب التوصيل">
                <p className="text-xs text-ghasla-text-secondary">
                  {assignedCourier || order.pickupCourier
                    ? "مندوب الاستلام معيّن لهذا الطلب"
                    : order.tab === "new"
                    ? "اقبل الطلب أولاً ثم عيّن المندوب من شريط الإجراءات بالأسفل"
                    : "لم يتم تعيين مندوب استلام بعد"}
                </p>
                <div className="mt-3 space-y-3">
                  {assignedCourier || order.pickupCourier ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F0F4F4] p-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E8F6F6] text-sm font-semibold text-ghasla-text-link">
                          {(assignedCourier || order.pickupCourier?.name || "").charAt(0)}
                        </span>
                        <div>
                          <p className="text-xs text-ghasla-text-secondary">مندوب الاستلام</p>
                          <p className="text-sm font-semibold text-ghasla-text-heading">
                            {assignedCourier || order.pickupCourier?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-[#E8F6F6] text-ghasla-text-link transition-opacity hover:opacity-80"
                          aria-label="اتصال بالمندوب"
                        >
                          <i className="ri-phone-line text-sm" />
                        </button>
                        <Badge tone={courierTone[order.pickupCourier?.statusTone ?? "teal"]}>
                          {order.pickupCourier?.status ?? "تم التعيين"}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm italic text-ghasla-text-secondary">لم يتم تعيين مندوب استلام بعد</p>
                  )}

                  {order.deliveryCourier ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F0F4F4] p-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0FDFD] text-sm font-semibold text-ghasla-text-link">
                          {order.deliveryCourier.name.charAt(0)}
                        </span>
                        <div>
                          <p className="text-xs text-ghasla-text-secondary">مندوب التسليم</p>
                          <p className="text-sm font-semibold text-ghasla-text-heading">{order.deliveryCourier.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-[#E8F6F6] text-ghasla-text-link transition-opacity hover:opacity-80"
                          aria-label="اتصال بالمندوب"
                        >
                          <i className="ri-phone-line text-sm" />
                        </button>
                        <Badge tone={courierTone[order.deliveryCourier.statusTone]}>
                          {order.deliveryCourier.status}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm italic text-ghasla-text-secondary">لم يتم تعيين مندوب تسليم بعد</p>
                  )}
                </div>
              </Section>
            </>
          )}

          {/* 5 — التفاصيل المالية */}
          {isLaundry ? (
            <Section icon="ri-wallet-3-line" title="التفاصيل المالية">
              <Row label={`المجموع الفرعي (${totalPieces} قطع)`}>
                <span className="font-medium">{order.laundrySubtotal} ر.س</span>
              </Row>
              <Row label={order.delivery?.type === "مستعجل" ? "التوصيل المستعجل" : "التوصيل العادي"}>
                <span className="font-medium">{order.delivery?.fee} ر.س</span>
              </Row>
              <div className="my-1 border-t border-[#F0F4F4]" />
              <Row label="إجمالي الطلب">
                <span className="font-medium">{order.amount} ر.س</span>
              </Row>
              <Row label="العمولة (15%)">
                <span className="font-medium text-[#E74C3C]">- {commission} ر.س</span>
              </Row>
              <div className="my-1 border-t border-[#F0F4F4]" />
              <Row label="صافي المستحق">
                <span className="text-base font-bold text-ghasla-text-link">{net} ر.س</span>
              </Row>
              {order.settlement && <p className="mt-2 text-xs text-ghasla-text-secondary">{order.settlement}</p>}
            </Section>
          ) : (
            <Section icon="ri-wallet-3-line" title="التفاصيل المالية">
              <Row label="إجمالي الطلب">
                <span className="font-medium">{order.amount} ر.س</span>
              </Row>
              <Row label="العمولة (15%)">
                <span className="font-medium text-[#E74C3C]">- {commission} ر.س</span>
              </Row>
              <div className="my-1 border-t border-[#F0F4F4]" />
              <Row label="صافي المستحق">
                <span className="text-base font-bold text-ghasla-text-link">{net} ر.س</span>
              </Row>
              <p className="mt-2 text-xs text-ghasla-text-secondary">{order.settlement ?? "—"}</p>
            </Section>
          )}

          {/* الإسناد (غير المغاسل) */}
          {!isLaundry && order.tab !== "new" && (
            <Section icon="ri-user-star-line" title="المنفذ">
              {assignedTech ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E8F6F6] text-sm font-semibold text-ghasla-text-link">
                      {assignedTech.charAt(0)}
                    </span>
                    <div>
                      <p className="text-[15px] font-semibold text-ghasla-text-heading">{assignedTech}</p>
                      <span className="flex items-center gap-1 text-xs text-[#27AE60]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#27AE60]" />
                        متاح
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAssignedTech("")}
                    className="cursor-pointer whitespace-nowrap text-sm font-medium text-ghasla-text-link hover:underline"
                  >
                    تغيير
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <select
                    value={selectedTech}
                    onChange={(e) => setSelectedTech(e.target.value)}
                    className="h-11 w-full cursor-pointer rounded-xl border border-[#E4F5F5] bg-white px-3 text-sm text-ghasla-text-heading outline-none transition-all focus:ring-2 focus:ring-[#00AAB7]/20"
                  >
                    <option value="">اختر الفني</option>
                    {teamMembers.map((m) => (
                      <option key={m.id} value={`${m.name} — ${m.specialty}`}>
                        {m.name} — {techStatus[m.status].label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAssign}
                    disabled={!selectedTech}
                    className="h-11 w-full cursor-pointer rounded-xl bg-[#00AAB7] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    إسناد
                  </button>
                </div>
              )}
            </Section>
          )}

          {/* 6 — صور قبل/بعد (تظهر في قيد التنفيذ والمكتمل فقط) */}
          {showPhotos && (
            <Section icon="ri-camera-line" title={photoCfg.title}>
              <p className="mb-2 text-[13px] text-ghasla-text-secondary">{photoCfg.before}</p>
              {beforePhotos.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {beforePhotos.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightbox(url)}
                      className="h-[100px] cursor-pointer overflow-hidden rounded-lg"
                    >
                      <img
                        src={url}
                        alt={`${photoCfg.before} ${i + 1} - غسلة`}
                        title={`${photoCfg.before} ${i + 1} - غسلة`}
                        className="h-full w-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex h-16 items-center justify-center rounded-xl border border-dashed border-[#E4F5F5] bg-[#FBFEFE] text-xs text-ghasla-text-secondary">
                  لم يتم التصوير بعد
                </div>
              )}

              {showAfter && (
                <>
                  <p className="mb-2 mt-4 text-[13px] text-ghasla-text-secondary">{photoCfg.after}</p>
                  {afterPhotos.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {afterPhotos.map((url, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(url)}
                          className="h-[100px] cursor-pointer overflow-hidden rounded-lg"
                        >
                          <img
                            src={url}
                            alt={`${photoCfg.after} ${i + 1} - غسلة`}
                            title={`${photoCfg.after} ${i + 1} - غسلة`}
                            className="h-full w-full object-cover object-top"
                          />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-16 items-center justify-center rounded-xl border border-dashed border-[#E4F5F5] bg-[#FBFEFE] text-xs text-ghasla-text-secondary">
                      لا توجد صور
                    </div>
                  )}
                </>
              )}
            </Section>
          )}

          {/* 7 — سجل الحالات */}
          <Section icon="ri-history-line" title="سجل الحالات" className="lg:col-span-2">
            <Timeline items={displayTimeline} />
          </Section>

          {/* 8 — التقييم */}
          {order.tab === "completed" && (
            <Section icon="ri-star-line" title="تقييم العميل">
              {order.rating ? (
                <div>
                  <div className="flex items-center gap-1" dir="ltr">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-[20px] ${
                          i <= order.rating ? "text-[#F59E0B]" : "text-[#D5E3E3]"
                        }`}
                      />
                    ))}
                  </div>
                  {order.ratingComment && (
                    <p className="mt-2 text-[15px] text-ghasla-text-primary">&ldquo;{order.ratingComment}&rdquo;</p>
                  )}
                  {isLaundry && order.courierBonus && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#E8F6F6] px-3 py-2 text-xs text-ghasla-text-link">
                      <i className="ri-truck-line" />
                      مكافأة المندوب: {order.courierBonus} ر.س (5%)
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm italic text-ghasla-text-secondary">العميل لم يقيّم بعد</p>
              )}
            </Section>
          )}
        </div>

      {/* Footer */}
      {order.tab !== "current" && order.tab !== "cancelled" && (
        <div className="mt-4 rounded-[20px] border border-[#F0F4F4] bg-white p-4 shadow-card">
            {order.tab === "new" && decision === null && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setDecision("accept")}
                  className="flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-l from-[#27AE60] to-[#2ECC71] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  <i className="ri-check-line" />
                  قبول
                </button>
                <button
                  type="button"
                  onClick={() => setDecision("reject")}
                  className="flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#E74C3C] bg-white px-4 text-sm font-semibold text-[#E74C3C] transition-colors hover:bg-[#FFF5F5]"
                >
                  <i className="ri-close-line" />
                  رفض
                </button>
              </div>
            )}
            {order.tab === "new" && decision === "accept" && (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-sm font-semibold text-[#27AE60]">
                  <i className="ri-checkbox-circle-fill text-lg" />
                  تم قبول الطلب بنجاح
                </div>

                <div className="border-t border-[#F0F4F4] pt-4">
                  <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-ghasla-text-heading">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
                      <i className={`${isLaundry ? "ri-truck-line" : "ri-user-star-line"} text-sm`} />
                    </span>
                    {isLaundry ? "تعيين مندوب الاستلام" : "تعيين المنفذ"}
                  </p>

                  {assignedName ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#F0FDFD] px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E8F6F6] text-sm font-semibold text-ghasla-text-link">
                          {assignedName.charAt(0)}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-ghasla-text-heading">{assignedName}</p>
                          <p className="text-xs text-[#27AE60]">تم التعيين بنجاح</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => (isLaundry ? setAssignedCourier("") : setAssignedTech(""))}
                        className="cursor-pointer whitespace-nowrap text-sm font-medium text-ghasla-text-link hover:underline"
                      >
                        تغيير
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Select
                        value={selectedAssignee}
                        onChange={setSelectedAssignee}
                        options={assignOptions}
                        placeholder={isLaundry ? "اختر المندوب" : "اختر المنفذ"}
                        icon={isLaundry ? "ri-truck-line" : "ri-user-star-line"}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleFlowAssign}
                        disabled={!selectedAssignee}
                        className="h-11 cursor-pointer whitespace-nowrap rounded-xl bg-[#00AAB7] px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {isLaundry ? "تعيين المندوب" : "تعيين المنفذ"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {order.tab === "new" && decision === "reject" && (
              <div className="flex items-center justify-center gap-2 py-2 text-sm font-medium text-[#E74C3C]">
                <i className="ri-close-circle-fill text-lg" />
                تم رفض الطلب
              </div>
            )}
            {order.tab === "scheduled" && (
              <button
                type="button"
                onClick={handleAssign}
                disabled={!selectedTech && !assignedTech}
                className="h-12 w-full cursor-pointer rounded-xl bg-[#00AAB7] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                إسناد للفني
              </button>
            )}
            {order.tab === "completed" && (
              <button
                type="button"
                onClick={() => navigate(`/provider/orders/${order.id}/invoice`)}
                className="h-12 w-full cursor-pointer rounded-xl border-[1.5px] border-[#00AAB7] bg-white px-4 text-sm font-semibold text-ghasla-text-link transition-colors hover:bg-[#F0FDFD]"
              >
                عرض الفاتورة
              </button>
            )}
          </div>
        )}

      {/* تتبع تنفيذ المهمة (حالي) */}
      {order.tab === "current" && (
        <div className="mt-4">
          <ExecutionFlow order={order} />
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-8"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt="صورة مكبرة - غسلة"
            className="max-h-full max-w-full rounded-2xl object-contain"
          />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-6 top-6 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="إغلاق الصورة"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>
      )}
    </div>
  );
}